import {HttpBackend, HttpClient} from '@angular/common/http';
import {isPlatformServer, PlatformLocation} from '@angular/common';
import {DOCUMENT, inject, Injectable, makeStateKey, PLATFORM_ID, TransferState} from '@angular/core';
import {TranslateLoader, TranslateService, TranslationObject} from '@ngx-translate/core';
import {firstValueFrom, of, tap} from 'rxjs';
import {LngKeys} from '@app/shared/enums';

/** Reuse exactly the translations that produced the server HTML during hydration. */
@Injectable()
export class InitialTranslationsLoader implements TranslateLoader {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly state = inject(TransferState);
  private readonly server = isPlatformServer(inject(PLATFORM_ID));

  getTranslation(language: string) {
    const key = makeStateKey<TranslationObject>(`translations:${language}`);
    if (this.state.hasKey(key)) {
      const translations = this.state.get(key, {});
      this.state.remove(key);
      return of(translations);
    }
    const prefix = this.server ? 'https://www.selfcv.com/assets/i18n/' : '/assets/i18n/';
    return this.http.get<TranslationObject>(`${prefix}${language}.json`).pipe(
      tap(translations => {
        if (this.server) this.state.set(key, translations);
      })
    );
  }
}

export function initializeTranslations(): Promise<TranslationObject> {
  const translate = inject(TranslateService);
  const document = inject(DOCUMENT);
  const segment = inject(PlatformLocation).pathname.split('/').filter(Boolean)[0];
  const language = Object.values(LngKeys).includes(segment as LngKeys) ? segment : LngKeys.EN;
  document.documentElement.lang = language;
  document.body.classList.add(`lang_${language}`);
  // Angular must not bind translate pipes until the selected dictionary is ready.
  return firstValueFrom(translate.use(language));
}
